/**
 * Deactivate a BFF user.
 *
 * Deactivating, not deleting: the account keeps everything the person wrote and approved, and can
 * be brought back from the Desativados tab. The copy says so, because "excluir" set the
 * expectation that the person would be gone.
 *
 * @module Pages/Settings/UserManagement
 */

import { css } from '@emotion/css';
import { UTButton, UTModal } from '@group-link-one/gl-ui/utilities';
import type { JSX } from 'react';
import type { User } from '../../Types/UserManagement.types';

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-ml);
  width: 100%;
  min-width: 0;
`;

const textStyle = css`
  font-size: var(--font-size-sm);
  line-height: var(--line-height-normal);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const emailStyle = css`
  font-family: var(--font-mono);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const errorStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-danger);
`;

const footerStyle = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-sm);
`;

export interface DeleteUserModalProps {
  open: boolean;
  user?: User;
  isDeleting: boolean;
  error?: string | null;
  onClose: () => void;
  onConfirm: (user: User) => void;
}

export const DeleteUserModal = ({
  open,
  user,
  isDeleting,
  error,
  onClose,
  onConfirm,
}: DeleteUserModalProps): JSX.Element => {
  const content = (
    <div className={bodyStyle}>
      <p className={textStyle}>
        Desativar <span className={emailStyle}>{user?.email}</span>? A pessoa perde o acesso na
        hora, mesmo que esteja com uma sessão aberta.
      </p>
      <p className={textStyle}>
        Nada do que ela criou ou aprovou é apagado, e ela continua na aba{' '}
        <strong>Desativados</strong>, de onde pode ser reativada com os mesmos papéis.
      </p>

      {error ? <span className={errorStyle}>{error}</span> : null}

      <div className={footerStyle}>
        <UTButton fitContent variant='tertiary' text='Cancelar' onClick={onClose} />
        <UTButton
          fitContent
          id='delete-user-confirm'
          text={isDeleting ? 'Desativando...' : 'Desativar usuário'}
          disabled={isDeleting}
          onClick={() => {
            if (user) onConfirm(user);
          }}
        />
      </div>
    </div>
  );

  return (
    <UTModal
      open={open}
      onClose={onClose}
      title='Desativar usuário'
      content={content}
      maxWidth='500px'
      hasButtonClose
      position='center'
    />
  );
};
